/**
 * Composable para estado y validación del formulario de emisión.
 * Maneja pasos, datos del formulario y carga de dirección por CP.
 */

import { reactive, ref, computed, watch } from 'vue';
import { getPostalCodes } from '@/services/api';
import { splitFullName } from '@/utils/emissionMappers';
import { useQuoteStore } from '@/stores/quoteStore';
import type { EmissionFormData } from '@/types/emission.types';

const initialFormData = (): EmissionFormData => ({
  paterno: '',
  materno: '',
  nombres: '',
  rfc: '',
  fechaNacimiento: '',
  telefonos: '',
  genero: '',
  email: '',
  calle: '',
  numeroExterior: '',
  numeroInterior: '',
  colonia: '',
  municipio: '',
  estado: '',
  cp: '',
  idEstado: undefined,
  idCiudad: undefined,
  idMunicipio: undefined,
  numeroSerie: '',
  numeroMotor: '',
  placas: '',
  aceptaPolitica: false,
  articulo492Aceptado: false,
  articulo492FechaAceptacion: '',
  articulo492DocumentosCargados: 0,
  articulo492DocumentosMetadata: '{}',
});

export function useEmissionForm() {
  const quoteStore = useQuoteStore();
  const quoteData = computed(() => quoteStore.quoteData);

  const formData = reactive<EmissionFormData>(initialFormData());
  const currentStep = ref(1);

  const vehicleDescription = computed(() => quoteData.value.model?.name ?? null);

  const canProceedStep1 = computed(
    () =>
      Boolean(
        formData.paterno.trim() &&
          formData.nombres.trim() &&
          formData.rfc.trim() &&
          formData.fechaNacimiento &&
          formData.telefonos.trim() &&
          formData.genero &&
          formData.email.trim()
      )
  );

  const canProceedStep2 = computed(
    () =>
      Boolean(
        formData.calle.trim() &&
          formData.numeroExterior.trim() &&
          formData.colonia.trim() &&
          formData.municipio.trim() &&
          formData.estado.trim() &&
          formData.cp.trim()
      )
  );

  const canProceedStep3 = computed(
    () =>
      Boolean(
        formData.numeroSerie.trim().length === 17 && formData.aceptaPolitica
      )
  );

  const canProceedStep4 = computed(
    () => Boolean(formData.articulo492Aceptado)
  );

  async function loadAddressFromPostalCode(postalCode: string): Promise<void> {
    if (!postalCode || postalCode.length < 4) return;
    const results = await getPostalCodes(postalCode);
    if (results.length === 0) {
      formData.idEstado = undefined;
      formData.idCiudad = undefined;
      formData.idMunicipio = undefined;
      return;
    }
    const first = results[0];
    formData.colonia = first.settlement ?? '';
    formData.municipio = first.municipality ?? '';
    formData.estado = first.state ?? '';
    formData.cp = first.code ?? postalCode;
    formData.idEstado = first.idEstado;
    formData.idCiudad = first.idCiudad;
    formData.idMunicipio = first.idMunicipio;
  }

  function initFromQuoteStore(): void {
    const personal = quoteData.value.personalData;
    if (!personal) return;

    formData.email = personal.email || '';
    formData.telefonos = personal.whatsapp || '';
    formData.genero = (personal.gender as 'M' | 'F') || '';
    formData.fechaNacimiento = personal.birthDate || '';
    
    // Solo procesar el nombre si no es el valor por defecto de Prospecto Rápido
    if (personal.fullName && personal.fullName !== 'Prospecto Rápido') {
      const parts = splitFullName(personal.fullName);
      formData.nombres = parts.nombres;
      formData.paterno = parts.paterno;
      formData.materno = parts.materno;
    } else {
      formData.nombres = '';
      formData.paterno = '';
      formData.materno = '';
    }
    if (personal.postalCode) {
      formData.cp = personal.postalCode;
      loadAddressFromPostalCode(personal.postalCode).then(() => {
        if (formData.idCiudad == null && personal.idCiudad != null) formData.idCiudad = personal.idCiudad;
        if (formData.idEstado == null && personal.idEstado != null) formData.idEstado = personal.idEstado;
        if (formData.idMunicipio == null && personal.idMunicipio != null) formData.idMunicipio = personal.idMunicipio;
      });
    }
  }

  function handleNextStep(): void {
    if (currentStep.value === 1 && canProceedStep1.value) currentStep.value = 2;
    else if (currentStep.value === 2 && canProceedStep2.value) currentStep.value = 3;
    else if (currentStep.value === 3 && canProceedStep3.value) currentStep.value = 4;
  }

  function handleBackStep(): void {
    if (currentStep.value > 1) currentStep.value -= 1;
  }

  watch(currentStep, (step) => {
    if (step === 2 && formData.cp && (!formData.colonia || !formData.municipio || !formData.estado)) {
      loadAddressFromPostalCode(formData.cp);
    }
  });

  watch(
    () => formData.cp,
    (cp) => {
      if (!cp || cp.length < 4) {
        formData.idEstado = undefined;
        formData.idCiudad = undefined;
        formData.idMunicipio = undefined;
      }
    }
  );

  watch(
    () => formData.cp,
    async (newCp) => {
      if (newCp && newCp.length >= 4 && currentStep.value === 2) {
        await loadAddressFromPostalCode(newCp);
      }
    },
    { immediate: false }
  );

  return {
    formData,
    quoteData,
    currentStep,
    vehicleDescription,
    canProceedStep1,
    canProceedStep2,
    canProceedStep3,
    canProceedStep4,
    loadAddressFromPostalCode,
    initFromQuoteStore,
    handleNextStep,
    handleBackStep,
  };
}
